import { HOURS_IN_DAY } from '@stdlib/constants-time';
import { Form, Layout, Switch } from 'antd';
import classNames from 'classnames';
import type { ValidateFields } from 'rc-field-form/es/interface';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import type { SDetailLayoutProps } from 'app/components/StaycationUI';
import { SCard, SDetailLayout } from 'app/components/StaycationUI';
import { CurrencyWrapper } from 'app/components/commons/Currency/CurrencyWrapper/CurrencyWrapper';
import { Delete } from 'app/components/commons/ExtraButtons/Delete/Delete';
import { CustomInput } from 'app/components/fields/Input';
import { Option, Select } from 'app/components/fields/Select';
import { useGetHotel } from 'app/hooks/data/hotels/useHotel';
import { useEmojis } from 'app/hooks/data/useEmojis';
import {
  useDeleteExperience,
  useExperiencesCategories,
} from 'app/hooks/data/useExperience';
import type { Experience } from 'app/typings/experiences';
import { useSearchParams } from 'app/utils/searchParams';
import { pluralize } from 'app/utils/strings';
import { parseInputNumber } from 'app/utils/typing';

import { AssetDetailTitle } from '../AssetDetailTitle/AssetDetailTitle';
import { Mode } from '../Hotel/Types';
import { MarkdownPreview } from '../MarkdownPreview/MarkdownPreview';
import { SwitchableInput } from '../SwitchableInput/SwitchableInput';

import { Availability } from './Availability/Availability';
import type {
  AvailableOn,
  AvailablityRestrictions,
} from './Availability/utils';
import { BasicInfo } from './BasicInfo/BasicInfo';
import './ExperienceForm.scss';
import { Photos } from './Photos/Photos';
import { PracticalInfo } from './PracticalInfo/PracticalInfo';
import { Value } from './Value/Value';
import { computeExperienceName, isCategoryDisabledForOriginals } from './utils';

const { Content } = Layout;

const TIME_UNIT = ['hours', 'days'];

type addOnLimitation = 'maxQuantity' | 'closedHoursBeforeCheckin';

type FormWrapperProps = {
  isBuilder?: boolean;
  layoutProps: SDetailLayoutProps;
  children: JSX.Element;
};

export type ExperienceFormValues = Experience & {
  availableDaysRadio: AvailablityRestrictions;
  availableOn: AvailableOn[];
};

const HOTEL_EXPERIENCE_TYPE = 1;
const ORIGINAL_EXPERIENCE_TYPE = 2;

export const FormWrapper = ({
  isBuilder,
  layoutProps,
  children,
}: FormWrapperProps) =>
  isBuilder ? (
    <div className="experiences-builder-form page-content">{children}</div>
  ) : (
    <SDetailLayout {...layoutProps}>
      <Content className="page-content experience-old-form">{children}</Content>
    </SDetailLayout>
  );

type FormPartWrapperProps = {
  isBuilder?: boolean;
  subtitle?: string;
  title: string;
  children: JSX.Element;
};

export const FormPartWrapper = ({
  isBuilder,
  title,
  subtitle,
  children,
}: FormPartWrapperProps) =>
  isBuilder ? (
    <div className="form-part">
      <h3>{title}</h3>
      {children}
    </div>
  ) : (
    <SCard title={title} subtitle={subtitle}>
      {children}
    </SCard>
  );

export const ExperienceForm = ({
  title,
  isBuilder,
  experienceId,
  mode,
  setMode,
  values,
  initialValues,
  setFieldsValue,
  resetFields,
  isFieldsTouched,
  submit,
  onCancel,
  isFormValid,
  validateFields,
}: Props) => {
  const { getSearchParam } = useSearchParams();

  const isOriginalQuery = getSearchParam('isOriginal');
  const queryValue =
    isOriginalQuery === 'true' ? ORIGINAL_EXPERIENCE_TYPE : undefined;

  const history = useHistory();
  const { data: experienceCategories } = useExperiencesCategories();
  const { data: emojis } = useEmojis();
  const { data: hotel } = useGetHotel(values.hotelId);
  const { mutateAsync: deleteExperience } = useDeleteExperience(experienceId);
  const [experienceType, setExperienceType] = useState(
    queryValue || HOTEL_EXPERIENCE_TYPE
  );
  const [addOnSwitchesStates, setAddOnSwitchesStates] = useState<
    { closedHoursBeforeCheckin: boolean; maxQuantity: boolean } | undefined
  >(undefined);

  const isOriginal = experienceType === ORIGINAL_EXPERIENCE_TYPE;

  useEffect(() => {
    if (experienceType === HOTEL_EXPERIENCE_TYPE && values.countryId) {
      setExperienceType(ORIGINAL_EXPERIENCE_TYPE);
    }
  }, [experienceType, values.countryId]);

  const category = useMemo(
    () => experienceCategories?.find((cat) => cat.id === values.categoryId),
    [values.categoryId, experienceCategories]
  );

  const isCategoryDisabled = useMemo(
    () =>
      isOriginal
        ? isCategoryDisabledForOriginals(initialValues?.categoryId)
        : undefined,
    [isOriginal, initialValues?.categoryId]
  );

  const computedExperienceName = useMemo(() => {
    return computeExperienceName({
      category: experienceCategories?.find(
        (cat) => cat.id === values.categoryId
      ),
      quantity: values.quantity,
      brand: values.brand,
      duration: values.duration,
      durationDescription: values.durationDescription,
      time: values.time,
      locations: values.locations,
    });
  }, [
    experienceCategories,
    values.quantity,
    values.categoryId,
    values.brand,
    values.duration,
    values.durationDescription,
    values.time,
    values.locations,
  ]);

  useEffect(() => {
    const emoji = emojis?.find((e) => e.id === category?.pictureId);

    if (emoji && !values.emoji) {
      setFieldsValue({
        ...values,
        emoji,
      });
    }
  }, [category, emojis, setFieldsValue, values]);

  const renderDeleteButton = () => {
    if (!experienceId) {
      return null;
    }

    return (
      <Delete
        key={'experience-delete-button'}
        entityDescription={`Experience #${experienceId}`}
        onDelete={async () => {
          await deleteExperience();
          history.push('/activities');
        }}
      />
    );
  };

  const onEdit = () => {
    if (setMode) {
      setMode(Mode.Edit);
    }
  };

  const renderMaximumQuantity = useCallback(() => {
    return (
      <div
        className={
          mode !== Mode.View || !!values.maxQuantity ? 'input-value' : ''
        }
      >
        {mode === Mode.View ? (
          !!values.maxQuantity && <div>{values.maxQuantity}</div>
        ) : (
          <>
            <Form.Item name="maxQuantity" noStyle>
              <CustomInput
                disabled={!addOnSwitchesStates?.maxQuantity}
                suffix={'max'}
                onWheel={(e) => e.currentTarget.blur()}
                min={0}
                parser={parseInputNumber}
              />
            </Form.Item>
          </>
        )}
        {(mode !== Mode.View || !!values.maxQuantity) && 'per booking'}
      </div>
    );
  }, [addOnSwitchesStates?.maxQuantity, mode, values]);

  const renderRemoveAvailability = useCallback(() => {
    const valueDisplay =
      values.addOnLimitAvailabilityUnit === 'days'
        ? pluralize(values.closedHoursBeforeCheckin, 'day', 'days')
        : pluralize(values.closedHoursBeforeCheckin, 'hour', 'hours');

    return (
      <div
        className={
          mode !== Mode.View || !!values.closedHoursBeforeCheckin
            ? 'input-value'
            : ''
        }
      >
        {mode === Mode.View ? (
          values.closedHoursBeforeCheckin && <div>{valueDisplay}</div>
        ) : (
          <div className="combine-input">
            <Form.Item name="closedHoursBeforeCheckin" noStyle>
              <CustomInput
                disabled={!addOnSwitchesStates?.closedHoursBeforeCheckin}
                onWheel={(e) => e.currentTarget.blur()}
                min={0}
                parser={parseInputNumber}
              />
            </Form.Item>
            <Form.Item name="addOnLimitAvailabilityUnit" noStyle>
              <Select
                disabled={!addOnSwitchesStates?.closedHoursBeforeCheckin}
                defaultValue={
                  values.closedHoursBeforeCheckin &&
                  values.closedHoursBeforeCheckin % HOURS_IN_DAY === 0
                    ? 'days'
                    : 'hours'
                }
              >
                {TIME_UNIT.map((timeUnit) => (
                  <Option key={timeUnit} value={timeUnit}>
                    {timeUnit}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        )}
        {(mode !== Mode.View || values.closedHoursBeforeCheckin) &&
          'before check-in'}
      </div>
    );
  }, [
    addOnSwitchesStates?.closedHoursBeforeCheckin,
    mode,
    values.addOnLimitAvailabilityUnit,
    values.closedHoursBeforeCheckin,
  ]);

  const addOnLimitationOption = useMemo(
    () => [
      {
        title: 'Maximum quantity',
        renderInput: renderMaximumQuantity(),
        value: initialValues?.maxQuantity
          ? initialValues?.maxQuantity
          : undefined,
        name: 'maxQuantity',
      },
      {
        title: 'Remove availability',
        renderInput: renderRemoveAvailability(),
        value: initialValues?.closedHoursBeforeCheckin
          ? initialValues?.closedHoursBeforeCheckin
          : undefined,
        disclaimer:
          'Users will not see this add-on if they want to book during this period.',
        name: 'closedHoursBeforeCheckin',
      },
    ],
    [
      renderMaximumQuantity,
      initialValues?.maxQuantity,
      initialValues?.closedHoursBeforeCheckin,
      renderRemoveAvailability,
    ]
  );

  useEffect(() => {
    setAddOnSwitchesStates({
      closedHoursBeforeCheckin:
        initialValues?.closedHoursBeforeCheckin !== undefined,
      maxQuantity: initialValues?.maxQuantity !== undefined,
    });
  }, [initialValues]);

  return (
    <FormWrapper
      isBuilder={isBuilder}
      layoutProps={{
        mode,
        onCancel,
        title: (
          <AssetDetailTitle
            assetTitle={computedExperienceName}
            hotelId={hotel?.id}
            hotelName={hotel?.name}
            isNewDataModel={false}
          />
        ),
        onEdit,
        onSave: submit,
        reset: resetFields,
        headerExtra: [renderDeleteButton()],
        isDirty:
          isFieldsTouched() ||
          !!(initialValues?.emoji?.id !== values?.emoji?.id) ||
          initialValues?.maxQuantity !== values.maxQuantity ||
          initialValues?.closedHoursBeforeCheckin !==
            values.closedHoursBeforeCheckin,
        isValid: isFormValid,
      }}
    >
      <CurrencyWrapper
        hotel={!isOriginal ? hotel : undefined}
        countryId={isOriginal ? values.countryId : undefined}
      >
        <div className="experience-basic-info">
          <div className="experience-basic-info-form">
            <FormPartWrapper isBuilder={isBuilder} title="Basic information">
              <BasicInfo
                isBuilder={isBuilder}
                disabled={!!experienceId}
                mode={mode}
                values={values}
                setFieldsValue={setFieldsValue}
                category={category}
                experienceCategories={experienceCategories ?? []}
                isCategoryDisabled={isCategoryDisabled}
                experienceType={experienceType}
                setExperienceType={setExperienceType}
                isOriginal={isOriginal}
              />
            </FormPartWrapper>
          </div>
          {!isBuilder && mode === 'edit' && (
            <MarkdownPreview
              // We need to do that because there's a typo in the old data model
              values={{
                ...values,
                importantInformation: values.importantInformations,
              }}
            />
          )}
        </div>
        {!!category && (
          <>
            {(category.spaces ||
              category.accessMode ||
              category.openingHours !== 'NOT_APPLICABLE' ||
              category.roomService) && (
              <FormPartWrapper
                isBuilder={isBuilder}
                title="Practical information"
                subtitle={
                  category.openingHours !== 'NOT_APPLICABLE'
                    ? 'Add practical information regarding the opening hours of the experience. This information will be shown at the bottom of the Package page. '
                    : undefined
                }
              >
                <PracticalInfo
                  mode={mode}
                  values={values}
                  setFieldsValue={setFieldsValue}
                  hotelSpaces={!isOriginal ? hotel?.spaces : undefined}
                  category={category}
                  validateFields={validateFields}
                />
              </FormPartWrapper>
            )}
            {!isOriginal && !isBuilder && (
              <FormPartWrapper
                isBuilder={isBuilder}
                title="Overnight rule"
                subtitle="Define when an always-included experience should be highlighted on the hotel page."
              >
                <Form.Item
                  label="Always included overnight:"
                  className="overnight-form-item"
                >
                  <Form.Item
                    name="isAlwaysIncludedWithNight"
                    valuePropName="checked"
                  >
                    <Switch
                      disabled={mode === Mode.View}
                      className={classNames([
                        { 'overnight-switch': mode === Mode.Edit },
                      ])}
                    />
                  </Form.Item>
                  {mode === Mode.Edit && (
                    <ul className="overnight-helper">
                      <li>
                        This will appear in the "Always Included" section on the
                        hotel page.
                      </li>
                      <li>
                        Apply this only to free hotel facilities available to
                        all overnight guests, except for F&B.
                      </li>
                    </ul>
                  )}
                </Form.Item>
              </FormPartWrapper>
            )}
            <FormPartWrapper isBuilder={isBuilder} title="Value">
              <Value
                mode={mode}
                values={values}
                category={category}
                isOriginal={isOriginal}
                setFieldsValue={setFieldsValue}
              />
            </FormPartWrapper>
            <FormPartWrapper
              isBuilder={isBuilder}
              title="Photos"
              subtitle="In edit mode, you can drag and drop photos to rearrange them. The first one will be the experience cover"
            >
              <Photos
                title={title}
                mode={mode}
                values={values}
                hotelId={!isOriginal ? hotel?.id : undefined}
                isBuilder={isBuilder}
                isOriginal={isOriginal}
              />
            </FormPartWrapper>
            {!isBuilder && (
              <FormPartWrapper
                isBuilder={isBuilder}
                title="Availability"
                subtitle="Configure days when the experience will be available for guests during their stay"
              >
                <Availability mode={mode} values={values} />
              </FormPartWrapper>
            )}
            {!isBuilder && (
              <FormPartWrapper
                isBuilder={isBuilder}
                title="Add-on limitations"
                subtitle="These settings are effective only if this experience is an add-on"
              >
                <div className="add-on-limitations-container">
                  {addOnLimitationOption.map((section, index) => (
                    <div
                      key={`${section.name}-${index}`}
                      className="sectionContainer"
                    >
                      <SwitchableInput
                        disabled={mode === Mode.View}
                        value={
                          addOnSwitchesStates?.[
                            section.name as addOnLimitation
                          ] ?? false
                        }
                        name={section.name}
                        title={section.title}
                        childNode={section.renderInput}
                        onChange={() => {
                          setFieldsValue({
                            [section.name]: undefined,
                          });
                          addOnSwitchesStates &&
                            setAddOnSwitchesStates({
                              ...addOnSwitchesStates,
                              [section.name]:
                                !addOnSwitchesStates?.[
                                  section.name as addOnLimitation
                                ],
                            });
                        }}
                        noBackground
                        noBorder
                      />
                    </div>
                  ))}
                </div>
              </FormPartWrapper>
            )}
          </>
        )}
      </CurrencyWrapper>
    </FormWrapper>
  );
};

type Props = {
  title?: string;
  isBuilder?: boolean;
  experienceId: number;
  mode: Mode;
  setMode?: (newMode: Mode) => void;
  values: ExperienceFormValues;
  initialValues?: ExperienceFormValues | null;
  setFieldsValue: (newValues: Partial<Experience>) => void;
  resetFields: () => void;
  isFieldsTouched: () => boolean;
  submit: () => void;
  onCancel: () => void;
  isFormValid: boolean;
  validateFields: ValidateFields;
};
